import { createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { get as getLocal, remove as removeLocal } from '~libs/localstorage';
import { CommonState, Row } from '~libs/reduxUtils';

import { authenticate, generateSession, getSettings, login, typePrefix } from '~main/actions/login';

const sessionKey = 'x-session-token';

interface SystemConfig {
  content?: {
    status?: string;
    heading?: string;
    text?: string;
  };
  last_updated?: string;
}

export interface User {
  id: number;
  first_name: string;
  surname: string;
  accounts_access_enabled: boolean;
  company_id: string;
  company: {
    id: number;
    name: string;
    logo: string;
    abn?: string;
    address?: string;
    suburb?: string;
    state?: string;
    postcode?: string;
  };
}

interface LinkedAccount extends User {
  full_name: string;
}

interface AuthState extends CommonState<Row> {
  authenticated: boolean;
  sessionToken: string | null;
  forceRedirect: boolean;
  settings: { [key: string]: string | number | boolean | [] };
  defaults?: { [key: string]: string | number | boolean };
  system?: SystemConfig;
  user?: User;
  tags?: string[];
  linkedAccounts?: LinkedAccount[];
  crossOriginToken?: string;
}

const initialState: AuthState = {
  loading: 'idle',
  error: null,
  authenticated: getLocal(sessionKey) ?? false,
  sessionToken: getLocal(sessionKey) ?? null,
  forceRedirect: false,
  rows: [],
  row: { id: -1 },
  settings: {},
  defaults: {},
  system: {},
  tags: [],
};

// Main slice, connecting API actions to redux state.
const slice = createSlice({
  name: 'login',
  initialState,
  reducers: {
    checkAuthenticated(state) {
      state.authenticated = getLocal(sessionKey) !== undefined;
    },
    removeAuthenticated(state) {
      state.authenticated = false;
      state.sessionToken = null;
      removeLocal(sessionKey);
    },
    setForceRedirect(state) {
      state.forceRedirect = true;
    },
    setSessionToken(state, action) {
      state.sessionToken = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.fulfilled, (state, action) => {
      state.authenticated = true;
      state.user = action.payload.data;
    });
    builder.addCase(authenticate.fulfilled, (state, action) => {
      state.authenticated = true;
      state.user = action.payload.data;
    });
    builder.addCase(generateSession.fulfilled, (state, action) => {
      state.crossOriginToken = action.payload.data;
    });
    builder.addCase(getSettings.fulfilled, (state, action) => {
      const { company, user, tags, defaults, system } = action.payload.data;

      state.settings = company;
      state.system = system;
      state.defaults = defaults;
      state.user = user;
      state.tags = tags;
      state.linkedAccounts = user.linked_accounts;
    });
    // Default matching for loading cases, pending when action is being called
    builder.addMatcher(isPending, (state, { type }) => {
      if (type.startsWith(`${typePrefix}/`)) state.loading = 'pending';
    });
    builder.addMatcher(isFulfilled, (state, { type }) => {
      if (type.startsWith(`${typePrefix}/`)) state.loading = 'fulfilled';
    });
    builder.addMatcher(isRejected, (state, action) => {
      if (action.type.startsWith(`${typePrefix}/`)) {
        state.loading = 'declined';
        state.error = action.error.message;
      }
    });
  },
});

export const { checkAuthenticated, removeAuthenticated, setForceRedirect, setSessionToken } = slice.actions;

export default slice.reducer;
